import { useEffect, useMemo, useState } from 'react'
import { getCulpritCandidates } from './api'
import { CulpritDraft, EMPTY_DRAFT, draftComplete, toCulpritFacts } from './culpritFacts'
import { disputeErrorFromException } from './errors'
import { PayoutCulpritFacts } from './requests.types'
import { CulpritCandidate } from './response.types'

/**
 * The culprit facts of a payout dispute success: loads the culprits with what each owed and what was
 * already remedied, holds what the operator enters, and says when every culprit is answered. A caller
 * that has already loaded the payout's candidates passes them as `loaded`, and nothing is fetched.
 */
export const usePayoutCulpritDrafts = (
  active: boolean,
  payoutToken: string | null,
  culpritTokens: string[],
  loaded?: CulpritCandidate[] | null
) => {
  const [fetched, setFetched] = useState<CulpritCandidate[] | null>(null)
  const allCandidates = loaded === undefined ? fetched : loaded
  const [drafts, setDrafts] = useState<Record<string, CulpritDraft>>({})
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!active || !payoutToken) {
      setFetched(null)
      setDrafts({})
      setLoadError(null)
      return
    }
    if (loaded !== undefined) return
    getCulpritCandidates(payoutToken)
      .then((r) => setFetched(r.data?.success && r.data.data ? r.data.data : []))
      .catch((e: unknown) => {
        setFetched([])
        setLoadError(disputeErrorFromException(e, 'Не удалось загрузить депозиты выплаты'))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, payoutToken])

  const key = culpritTokens.join(',')
  const candidates = useMemo(
    () => (allCandidates ?? []).filter((c) => culpritTokens.includes(c.token)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCandidates, key]
  )

  const draftOf = (token: string): CulpritDraft => drafts[token] ?? EMPTY_DRAFT
  const complete =
    candidates.length > 0 && candidates.every((c) => draftComplete(c, draftOf(c.token)))
  const facts: PayoutCulpritFacts[] = toCulpritFacts(candidates, drafts)

  return {
    loading: active && allCandidates === null,
    loadError,
    candidates,
    draftOf,
    setDraft: (token: string, draft: CulpritDraft) =>
      setDrafts((prev) => ({ ...prev, [token]: draft })),
    complete,
    facts,
  }
}
